// Large formatting editor for master content. Stores plain structured text that every output already
// understands: one line per paragraph / list item, table rows as tab-separated cells.
import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import { List, ListOrdered, Table as TableIcon, Rows3, Columns3, Trash2, Undo2, Redo2 } from 'lucide-react';

const esc = (t) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const BULLET = /^\s*(?:[-•*●▪]\s+)/;
const NUMBER = /^\s*\d+[.)]\s+/;

// plain text -> editor HTML (consecutive tab rows become one table, bullet/number runs become lists)
export const textToHtml = (text) => {
  const lines = String(text || '').split('\n').filter((l) => l.trim());
  let html = '';
  let i = 0;
  while (i < lines.length) {
    const l = lines[i];
    if (l.includes('\t')) {
      const rows = [];
      while (i < lines.length && lines[i].includes('\t')) rows.push(lines[i++].split('\t'));
      const w = Math.max(...rows.map((r) => r.length));
      html += '<table>' + rows.map((r, ri) => '<tr>' + Array.from({ length: w }, (_, c) => {
        const tag = ri === 0 ? 'th' : 'td';
        return `<${tag}><p>${esc((r[c] || '').trim())}</p></${tag}>`;
      }).join('') + '</tr>').join('') + '</table>';
      continue;
    }
    const kind = BULLET.test(l) ? 'ul' : NUMBER.test(l) ? 'ol' : null;
    if (kind) {
      const re = kind === 'ul' ? BULLET : NUMBER;
      const items = [];
      while (i < lines.length && re.test(lines[i]) && !lines[i].includes('\t')) items.push(lines[i++].replace(re, ''));
      html += `<${kind}>` + items.map((t) => `<li><p>${esc(t)}</p></li>`).join('') + `</${kind}>`;
      continue;
    }
    html += `<p>${esc(l.trim())}</p>`;
    i += 1;
  }
  return html || '<p></p>';
};

// editor JSON -> plain text
const inline = (n) => (n.content || []).map((c) => (c.type === 'text' ? c.text : c.type === 'hardBreak' ? ' ' : inline(c))).join('');
export const docToText = (doc) => {
  const out = [];
  (doc.content || []).forEach((n) => {
    if (n.type === 'table') {
      (n.content || []).forEach((row) => {
        const cells = (row.content || []).map((cell) => inline(cell).replace(/\t/g, ' ').trim());
        if (cells.some(Boolean)) out.push(cells.length > 1 ? cells.join('\t') : `${cells[0]}\t`);
      });
    } else if (n.type === 'bulletList' || n.type === 'orderedList') {
      (n.content || []).forEach((li, k) => out.push(`${n.type === 'bulletList' ? '- ' : `${k + 1}. `}${inline(li).trim()}`));
    } else {
      const t = inline(n).trim();
      if (t) out.push(t);
    }
  });
  return out.join('\n');
};

const Tool = ({ onClick, active, label, testid, children }) => (
  <button type="button" onClick={onClick} title={label} aria-label={label} data-testid={testid}
    className={`p-1.5 rounded hover:bg-gray-100 transition-colors ${active ? 'bg-gray-200 text-navy' : 'text-gray-600'}`}>
    {children}
  </button>
);

export const RichContentEditor = ({ value, onChange }) => {
  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: false, codeBlock: false, blockquote: false, code: false, horizontalRule: false }),
      Table.configure({ resizable: false }), TableRow, TableHeader, TableCell],
    content: textToHtml(value),
    onUpdate: ({ editor: e }) => onChange(docToText(e.getJSON())),
  });
  useEffect(() => {
    if (editor && docToText(editor.getJSON()) !== (value || '')) editor.commands.setContent(textToHtml(value), false);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!editor) return null;
  const c = () => editor.chain().focus();
  const inTable = editor.isActive('table');
  return (
    <div className="border rounded-md" data-testid="rich-content-editor">
      <div className="flex flex-wrap items-center gap-1 border-b px-2 py-1 bg-gray-50">
        <Tool label="Bullet list" testid="rte-bullets" active={editor.isActive('bulletList')} onClick={() => c().toggleBulletList().run()}><List className="w-4 h-4" /></Tool>
        <Tool label="Numbered list" testid="rte-numbers" active={editor.isActive('orderedList')} onClick={() => c().toggleOrderedList().run()}><ListOrdered className="w-4 h-4" /></Tool>
        <span className="w-px h-5 bg-gray-300 mx-1" />
        <Tool label="Insert table" testid="rte-table" onClick={() => c().insertTable({ rows: 2, cols: 3, withHeaderRow: true }).run()}><TableIcon className="w-4 h-4" /></Tool>
        {inTable && <>
          <Tool label="Add row" testid="rte-add-row" onClick={() => c().addRowAfter().run()}><Rows3 className="w-4 h-4" /></Tool>
          <Tool label="Add column" testid="rte-add-col" onClick={() => c().addColumnAfter().run()}><Columns3 className="w-4 h-4" /></Tool>
          <Tool label="Delete row" testid="rte-del-row" onClick={() => c().deleteRow().run()}><Trash2 className="w-4 h-4" /></Tool>
          <button type="button" className="text-xs text-red-600 px-1.5 hover:underline" data-testid="rte-del-table" onClick={() => c().deleteTable().run()}>Remove table</button>
        </>}
        <span className="flex-1" />
        <Tool label="Undo" testid="rte-undo" onClick={() => c().undo().run()}><Undo2 className="w-4 h-4" /></Tool>
        <Tool label="Redo" testid="rte-redo" onClick={() => c().redo().run()}><Redo2 className="w-4 h-4" /></Tool>
      </div>
      <EditorContent editor={editor} data-testid="rte-content"
        className="rte max-h-[55vh] min-h-[300px] overflow-y-auto px-3 py-2 text-sm" />
    </div>
  );
};

export default RichContentEditor;
